import { BaseAdminOperations } from "../../BaseAdminOperations";
import { Observable, Observer } from "rxjs";
import { IOrganisation, IPushNotification, 
    NotificationAppUrlSection, INewsMenuSection, IMoreMenuSection, 
    NotificationAppUrlItem, 
    ICourse } from "../../../../model/ModelTypes";
import { DatabaseKey } from "../../../../repository/IDataStore";
import { flatMap, map } from "rxjs/operators";
import { isNullOrUndefined } from "util";
export class CoursesAppUrlItem {
    label: string;
    code: string;
    appUrl: string;
    organisationCode: string;
    ageGroups: string[];
    courseModules: {
        label: string;
        appUrl: string;
        pages: {label: string, appUrlFunction: (roleName: string) => string}[]
    }[];
    constructor(course: ICourse, organisationCode: string) {
        this.label = course.name;
        this.organisationCode = organisationCode;
        this.code = course.code;
        this.appUrl = `is-${organisationCode.toUpperCase()}://${this.code.toUpperCase()}`;
        this.ageGroups = course.age_group;
        this.courseModules = course.modules.map(
            courseModule => {
                return {
                    label: courseModule.name,
                    appUrl: `is-${organisationCode.toUpperCase()}://${this.code.
                    toUpperCase()}/${courseModule.code.toUpperCase()}`,
                    pages: courseModule.pages.map(
                        modulePage => {
                            let encodedCourseName = course.name.replace(new RegExp(" ", "g"), "%20");
                            let encodedCoursePage = modulePage.title.replace(new RegExp(" ", "g"), "%20");
                            return {
                                label: modulePage.title,
                                appUrlFunction: (roleName: string) => {
                                    return `is-${organisationCode.toUpperCase()}://
                                    ${this.code.toUpperCase()}/
                                    ${courseModule.code.toUpperCase()}/
                                    ${roleName.replace(new RegExp(" ", "g"), "")}/
                                    ${encodedCourseName}/${encodedCoursePage}`;
                                }
                            };
                        }
                    )
                };
            }
        );
    }
}
export interface TestingAppUrlItem extends NotificationAppUrlItem {
    ageGroups: string[];
    modules: NotificationAppUrlItem[];
}
/**
 * Implements business logic for Operations avialable for access managements.
 */
export class PushNotificationsOperations extends BaseAdminOperations {
    errorCallback: Function;
    // tslint:disable-next-line:no-empty
    onInitializationComplete() {
        
    }
    observeQueuedPushNotifications = () => this.observeOrganisationChanges()
    .pipe(
        map(
            organisation => organisation.queued_push_notifications
        )    
    )
    observeProcessedPushNotifications = () => this.observeOrganisationChanges()
    .pipe(
        map(
            organisation => organisation.processed_push_notifications
        )    
    )
    observeAvialableRoles = () => this.observeOrganisationChanges()
    .pipe(
        map(organisation => organisation.roles)
    )
    observeAvialableGroups = () => this.observeOrganisationChanges()
    .pipe(
        map(organisation => organisation.groups)
    )
    observeAvialableAppUrls = ():
    Observable<NotificationAppUrlSection[]> => this.observeOrganisationChanges()
    .pipe(
        map(
            organisation => {
                if (!isNullOrUndefined(organisation.appData) &&
                    !isNullOrUndefined(organisation.appData.menu)) {
                    return  [
                        this.buildNewsNotificationSections(organisation.appData.menu.news, organisation.code),
                        this.buildMoreNotificationSections(organisation.appData.menu.more, this.organisationCode)
                    ];
                    
                } else {
                    return  [
                        this.buildNewsNotificationSections(undefined, organisation.code),
                        this.buildMoreNotificationSections(undefined, this.organisationCode)
                    ];
                }
            }
        )
    )
    observeAvialableTestAppUrls = ():
    Observable<TestingAppUrlItem[]> => this.observeOrganisationChanges()
    .pipe(
        map(
            organisation => this.buildDigitalTestSections(organisation.courses, organisation.code)
        )
    )
    observeAvialableQuizAppUrls = ():
    Observable<TestingAppUrlItem[]> => this.observeOrganisationChanges()
    .pipe(
        map(
            organisation => this.buildQuizSections(organisation.courses, organisation.code)
        )
    )
    observeAvialableCoursesAppUrls = ():
    Observable<CoursesAppUrlItem[]> => this.observeOrganisationChanges()
    .pipe(
        map(
            organisation => this.buildCoursesAppUrlSection(organisation.courses, organisation.code)
        )
    )
    deleteQueuedNotification(notification: IPushNotification): Observable<IOrganisation> {
        return Observable.create(
            async (observer: Observer<IOrganisation>) => {
                let response = await this.adminApi.getAuthenticatedHttpClient()
                .getInstance().delete(
                    `/api/organisations/${this.organisationCode.toLowerCase()}/push`,
                    {
                        data: {notificationIds: [notification.id]}
                    }
                );
                let deletedNotifications: string[] = response.data;
                if (
                    response.status === 200 && 
                    !isNullOrUndefined(deletedNotifications) &&
                    deletedNotifications.length > 0
                ) {
                  this.removePushNotificationFromOrganisation(deletedNotifications)
                    .subscribe(
                        result => {
                            observer.next(this.organisation);
                            return observer.complete();
                        },
                        error => {
                            this.reportError(error);
                            observer.error(error);
                            return observer.complete();
                        } 
                    );
                } else {
                    observer.error(new Error("Failed to Delete Notification"));
                    return observer.complete();
                }        
            }
        );
    }
  
    observeOrganisationChanges(): Observable<IOrganisation> {
        return this.dataStore.getDocuments<Observable<IOrganisation>>(DatabaseKey.organisation)
        .pipe(
            flatMap(item => item),
            map( item => {
                this.organisation = item;
                return item;
            })
        );
    }
    modifyOrganisationCode(organisationCode: string) {
        this.organisationCode = organisationCode;
    }
    sendPushNotification(pushNotification: IPushNotification): Observable<void> {
        return Observable.create(
            async (observer: Observer<void>) => {
                try {
                    let result = await this.adminApi.getAuthenticatedHttpClient()
                    .getInstance()
                    .post(`/api/organisations/${this.organisationCode}/push`, pushNotification);
                    if (result.status === 200) {
                        let createdPushNotification: IPushNotification = result.data;
                        if (!isNullOrUndefined(createdPushNotification.sending_time) &&
                            createdPushNotification.sending_time !== "") {
                            if (isNullOrUndefined(this.organisation.queued_push_notifications)) {
                                this.organisation.queued_push_notifications = [];
                            }
                            this.organisation.queued_push_notifications.unshift(createdPushNotification);
                        } else {
                            if (isNullOrUndefined(this.organisation.processed_push_notifications)) {
                                this.organisation.processed_push_notifications = [];
                            }
                            this.organisation.processed_push_notifications.unshift(createdPushNotification);
                        }
                        this.dataStore.insertDocument<IOrganisation>(this.organisation, DatabaseKey.organisation)
                        .subscribe(
                            insertResult => {
                                let voidFunction = () => { return; };
                                observer.next( voidFunction() );
                            },
                            error => {
                                observer.error(error);
                                observer.complete();
                            }
                        );
                    }                    
                } catch (error) {
                    observer.error(error);
                    observer.complete();
                }

            }
        );
    }
    private removePushNotificationFromOrganisation(deletedNotificationIds: string[])
    : Observable<Observable<IOrganisation>> {
        if (!isNullOrUndefined(this.organisation) && !isNullOrUndefined(this.organisation.queued_push_notifications)
            && !isNullOrUndefined(this.organisation.processed_push_notifications)) {
            deletedNotificationIds.forEach(
                notificationId => {
                    let notificationIndex =
                        this.organisation.processed_push_notifications
                        .findIndex( item => item.id === notificationId);
                    if (notificationIndex > -1) {
                        this.organisation.processed_push_notifications.splice(notificationIndex, 1);
                    } else {
                        notificationIndex = this.organisation.queued_push_notifications
                            .findIndex(item => item.id === notificationId
                            );
                        if (notificationIndex > -1) {
                            this.organisation.queued_push_notifications.splice(notificationIndex, 1);
                        }
                    }
                }
            );
            return this.dataStore.insertDocument<IOrganisation>(this.organisation, DatabaseKey.organisation);
        }
    }
    private buildNewsNotificationSections = (newsMenuSection: INewsMenuSection, organisationCode: string):
    NotificationAppUrlSection => {
        let newsSection: NotificationAppUrlSection;
        if (!isNullOrUndefined(newsMenuSection) &&
            !isNullOrUndefined(newsMenuSection.items) &&
            newsMenuSection.items.length > 0) {
            newsSection = {
                name: "RSS Feeds",
                items: newsMenuSection.items
                .map(
                    menuItem => {
                        return {
                            roles: menuItem.roles,
                            label: menuItem.name,
                            requiresLogin: menuItem.requiresLogin,
                            appUrl: `is-${organisationCode.toUpperCase()}://${
                                menuItem.name.toLowerCase().replace(" ", "-")}`,
                            extras: isNullOrUndefined(menuItem.notificationQueryParams) ? [] :
                            menuItem.notificationQueryParams.map(
                                item => {
                                    return {
                                        label: "Article Link",
                                        key: item
                                    };
                                }
                            )
                        };    
                    }
                )
            };
        } else {
            newsSection = {
                name: "RSS Feeds",
                items:  [{
                            roles: [],
                            requiresLogin: "true",
                            label: "News",
                            appUrl: `is-${organisationCode.toUpperCase()}://news`,
                            extras: [{
                                label: "Article Link",
                                key: "link"
                            }]
                        }]
                    
            }; 
        }
        return newsSection;
    }
    private buildMoreNotificationSections = 
    (moreMenuSection: IMoreMenuSection, organisationCode: string): NotificationAppUrlSection => {
        if (!isNullOrUndefined(moreMenuSection) &&
            !isNullOrUndefined(moreMenuSection.items) 
            // && moreMenuSection.items.length > 0
        ) {
            return {
                name: "More",
                items: moreMenuSection.items
                .map(
                    menuItem => {
                        return {
                            label: menuItem.name,
                            roles: menuItem.roles,
                            requiresLogin: menuItem.requiresLogin,
                            appUrl: `is-${organisationCode.toUpperCase()}://${
                                menuItem.name.toLowerCase().replace(new RegExp(" ", "g"), "-")}`,
                                extras: isNullOrUndefined(menuItem.notificationQueryParams) ? [] :
                            menuItem.notificationQueryParams.map(
                                item => {
                                    return {
                                        label: item,
                                        key: item 
                                    };
                                }
                            )
                        };    
                    }
                )
            };
        } 
        // else {
        //     return {
        //         name: "General",
        //         items: [
        //             {
        //                 roles: [],
        //                 label: "H2bsafer Updates",
        //                 requiresLogin: "true",
        //                 appUrl: `is-${organisationCode.toUpperCase()}://h2bsafer-updates`,
        //                 extras: [{
        //                     label: "Video Code",
        //                     key: "code"
        //                 }]
        //             },
        //             {
        //                 roles: [],
        //                 requiresLogin: "true",
        //                 label: "safety-centre",
        //                 appUrl: `is-${organisationCode.toUpperCase()}://safety-centre`,
        //                 extras: [{
        //                     label: "Article Link",
        //                     key: "link"
        //                 }]
        //             },
        //             {
        //                 roles: [],
        //                 requiresLogin: "true",
        //                 label: "Digital Homework",
        //                 appUrl: `is-${organisationCode.toUpperCase()}://digital-homework`,
        //                 extras: [{
        //                     label: "Article Link",
        //                     key: "link"
        //                 }]
        //             }
        //         ]
        //     };
        // }
    }
    private buildDigitalTestSections = 
    (courses: ICourse[], organisationCode: string): TestingAppUrlItem[] => {
        let coursesWithTests = courses.filter(
            course => {
                if (isNullOrUndefined(course.modules) || course.modules.length === 0) {
                    return false;
                }
                let modulesWithTests = course.modules.filter(
                    courseModule => !isNullOrUndefined(courseModule.test)
                );
                return !isNullOrUndefined(modulesWithTests) && modulesWithTests.length > 0;
            }
        );
        return coursesWithTests.map(
            course => {
                let modulesWithTest  = course.modules.filter(item => !isNullOrUndefined(item.test));
                return {
                  ageGroups: course.age_group,
                  extras: [],
                  roles: [],
                  requiresLogin: "true",
                  label: course.name,
                  appUrl: `is-${organisationCode.toUpperCase()}://${course.code.toUpperCase()}`,
                  modules: modulesWithTest.map(
                      coursModule => {
                        return {
                            roles: [],
                            label: coursModule.name,
                            appUrl: 
                            // modulesWithTest.length === 1 && coursesWithTests.length === 1 ? 
                            // `is-${organisationCode.toUpperCase()}://digital-test` :
                            `is-${organisationCode.toUpperCase()}://digital-test-${course.code
                            .toUpperCase()}-${coursModule.code
                                .toUpperCase()}`,
                            extras: [],
                            requiresLogin: "true"
                        };
                      }
                  )
                };
            }
        );
    }
    private buildQuizSections = 
    (courses: ICourse[], organisationCode: string): TestingAppUrlItem[] => {
        let coursesWithQuizes = courses.filter(
            course => {
                if (isNullOrUndefined(course.modules) || course.modules.length === 0) {
                    return false;
                }
                let modulesWithQuizes = course.modules.filter(
                    module => !isNullOrUndefined(module.quiz)
                );
                return !isNullOrUndefined(modulesWithQuizes) && modulesWithQuizes.length > 0;
            }
        );
        return coursesWithQuizes.map(
            course => {
                let modulesWithQuizes  = course.modules.filter(item => !isNullOrUndefined(item.quiz));
                return {
                  ageGroups: course.age_group,
                  roles: [],
                  label: course.name,
                  requiresLogin: "true",
                  extras: [],
                  appUrl: `is-${organisationCode.toUpperCase()}://${course.code.toUpperCase()}`,
                  modules: modulesWithQuizes
                  .map(
                      coursModule => {
                        return {
                            extras: [],
                            roles: [],
                            requiresLogin: "true",
                            label: coursModule.name,
                            appUrl: 
                            // modulesWithQuizes.length === 1 && coursesWithQuizes.length === 1 ? 
                            // `is-${organisationCode.toUpperCase()}://confidence-test` :
                            `is-${organisationCode.toUpperCase()}://confidence-test-${course.code
                            .toUpperCase()}-${coursModule.code.
                                toUpperCase()}`
                        };
                      }
                  )
                };
            }
        );
    }
    private buildCoursesAppUrlSection =
    (courses: ICourse[], organisationCode: string): CoursesAppUrlItem[] => {
        return courses.map(course => new CoursesAppUrlItem(course, organisationCode));
    } 
}


// WEBPACK FOOTER //
// ./src/utility/admin/OrganisationOperations/PushNotificationsOperations/PushNotificationsOperations.ts